import { scopedTranslation } from '@utils/I18n'

import type { PublicEvent } from './publicEvent'

const t = scopedTranslation('embeds.sample_events')

const EVENT_TYPES = ['meeting', 'campaigning', 'training'] as const

type Sample = {
  key: string
  type: (typeof EVENT_TYPES)[number]
  venue?: { key: string; city: string; state: string; postcode: string; lat: number; lng: number }
  inDays: number
  hours: [start: number, end: number]
  days?: number
  spots?: { total: number; filled: number }
  intro?: boolean
}

const SAMPLES: Sample[] = [
  {
    key: 'community_meeting',
    type: 'meeting',
    intro: true,
    venue: { key: 'town_hall', city: 'Melbourne', state: 'VIC', postcode: '3000', lat: -37.8136, lng: 144.9631 },
    inDays: 5,
    hours: [10, 12],
  },
  {
    key: 'early_voting',
    type: 'campaigning',
    venue: { key: 'voting_centre', city: 'Richmond', state: 'VIC', postcode: '3121', lat: -37.8183, lng: 144.9981 },
    inDays: 8,
    hours: [8, 18],
    days: 12,
    spots: { total: 24, filled: 9 },
  },
  {
    key: 'street_stall',
    type: 'campaigning',
    venue: { key: 'local_market', city: 'Fitzroy', state: 'VIC', postcode: '3065', lat: -37.7986, lng: 144.979 },
    inDays: 13,
    hours: [9, 13],
    spots: { total: 8, filled: 2 },
  },
  { key: 'online_training', type: 'training', inDays: 15, hours: [19, 20] },
  {
    key: 'doorknock',
    type: 'campaigning',
    intro: true,
    venue: { key: 'meeting_point', city: 'Brunswick', state: 'VIC', postcode: '3056', lat: -37.7676, lng: 144.9621 },
    inDays: 19,
    hours: [10, 14],
    spots: { total: 10, filled: 6 },
  },
  {
    key: 'volunteer_weekend',
    type: 'training',
    venue: { key: 'camp_ground', city: 'Daylesford', state: 'VIC', postcode: '3460', lat: -37.3417, lng: 144.1426 },
    inDays: 21,
    hours: [9, 16],
    days: 2,
  },
  {
    key: 'park_clean_up',
    type: 'campaigning',
    venue: { key: 'local_park', city: 'Footscray', state: 'VIC', postcode: '3011', lat: -37.7996, lng: 144.8996 },
    inDays: 26,
    hours: [9, 12],
    spots: { total: 10, filled: 8 },
  },
  {
    key: 'information_night',
    type: 'meeting',
    venue: { key: 'library', city: 'Geelong', state: 'VIC', postcode: '3220', lat: -38.1485, lng: 144.3598 },
    inDays: 31,
    hours: [18, 20],
  },
]

function at(today: Date, inDays: number, hour: number) {
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + inDays, hour).toISOString()
}

export function sampleEvents(): PublicEvent[] {
  const today = new Date()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return SAMPLES.map(({ key, type, venue, inDays, hours, days = 1, spots, intro }, index) => {
    const counts = spots ?? { total: 0, filled: 0 }
    return {
      id: -(index + 1),
      external_id: null,
      name: t(`events.${key}`),
      intro: intro ? t(`intros.${key}`) : null,
      url: null,
      tags: [],
      starts_at: at(today, inDays, hours[0]),
      ends_at: at(today, inDays + days - 1, hours[1]),
      time_zone: timeZone,
      online: !venue,
      event_type: { id: -(EVENT_TYPES.indexOf(type) + 1), name: t(`event_types.${type}`) },
      group: null,
      venue: venue
        ? {
            name: t(`venues.${venue.key}`),
            one_line: `${venue.city} ${venue.state} ${venue.postcode}`,
            address: { line1: null, city: venue.city, state: venue.state, postcode: venue.postcode },
            lat: venue.lat,
            lng: venue.lng,
          }
        : { name: null, one_line: null, address: null, lat: null, lng: null },
      more_dates_count: 0,
      series_id: -(index + 1),
      series_name: null,
      rsvps_count: 0,
      spots: { ...counts, self_sign_up: counts },
    }
  })
}
